import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { meApi } from '../api/endpoints.js';
import { AuthenticatedImage } from './AuthenticatedImage.jsx';
import { ScreenshotLightbox } from './ScreenshotLightbox.jsx';

/**
 * Inline preview of the most recent 4 screenshots from today.
 * Mounted on the employee dashboard. Click any thumbnail to open the
 * shared lightbox; "View all today's →" deep-links to the full page.
 *
 * Empty state explains the agent will start capturing once the user is
 * checked in.
 */
const PREVIEW_COUNT = 4;

export function TodaysScreenshotsPreview() {
    const today = useMemo(
        () => DateTime.now().setZone('Asia/Karachi').toISODate(),
        // Recompute when the date rolls (we don't bother polling for that;
        // a page reload at next sign-in will fix it).
        []
    );

    const query = useQuery({
        queryKey: ['me', 'screenshots', today, today],
        queryFn: () => meApi.getMyScreenshots({ start: today, end: today }),
        refetchInterval: 60_000,
    });

    const all = useMemo(() => {
        if (!query.data?.days) return [];
        return query.data.days.flatMap((d) => d.screenshots);
    }, [query.data]);

    const preview = all.slice(0, PREVIEW_COUNT);
    const total = query.data?.total ?? 0;

    const [lightbox, setLightbox] = useState({ open: false, flatIndex: 0 });
    const openAt = (id) => {
        const idx = all.findIndex((s) => s.id === id);
        if (idx >= 0) setLightbox({ open: true, flatIndex: idx });
    };

    return (
        <section className="surface-card" style={{ padding: '1rem 1.25rem', marginBottom: 24 }}>
            <div
                className="flex items-center justify-between"
                style={{ marginBottom: 12 }}
            >
                <div>
                    <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>
                        Today's screenshots
                    </p>
                    <p style={{
                        fontSize: 12,
                        color: 'var(--color-text-tertiary)',
                        margin: 0,
                        marginTop: 2,
                    }}>
                        {total === 0
                            ? 'Nothing captured yet'
                            : `${total} captured today`}
                    </p>
                </div>
                <Link
                    to="/my-screenshots"
                    style={{ fontSize: 12, color: 'var(--color-text-info)' }}
                >
                    View all today's →
                </Link>
            </div>

            {query.isLoading && (
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    Loading…
                </p>
            )}
            {query.isError && (
                <p style={{ fontSize: 13, color: 'var(--color-danger)' }}>
                    Could not load screenshots.
                </p>
            )}
            {query.isSuccess && preview.length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                    No screenshots yet today — the desktop agent will start capturing
                    once you check in.
                </p>
            )}

            {query.isSuccess && preview.length > 0 && (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${PREVIEW_COUNT}, 1fr)`,
                        gap: 8,
                    }}
                >
                    {preview.map((s) => (
                        <PreviewThumb key={s.id} shot={s} onOpen={openAt} />
                    ))}
                </div>
            )}

            <ScreenshotLightbox
                open={lightbox.open}
                screenshots={all}
                currentIndex={lightbox.flatIndex}
                onClose={() => setLightbox({ open: false, flatIndex: 0 })}
                onNavigate={(i) => setLightbox({ open: true, flatIndex: i })}
            />
        </section>
    );
}

function PreviewThumb({ shot, onOpen }) {
    const dt = DateTime.fromISO(shot.timestamp).setZone('Asia/Karachi');
    return (
        <button
            onClick={() => onOpen(shot.id)}
            style={{
                background: 'var(--color-bg-secondary)',
                border: '0.5px solid var(--color-border-tertiary)',
                borderRadius: 'var(--border-radius-md)',
                padding: 0,
                cursor: 'pointer',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left',
            }}
            title={`${shot.active_app || '(no app)'} · ${dt.toFormat('HH:mm:ss')}`}
        >
            <div style={{ width: '100%', aspectRatio: '16 / 9', background: '#000' }}>
                <AuthenticatedImage
                    screenshotId={shot.id}
                    alt={shot.active_app}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                    }}
                />
            </div>
            <div style={{ padding: '5px 7px', fontSize: 11 }}>
                <div className="tnum" style={{
                    color: 'var(--color-text-primary)',
                    fontWeight: 500,
                }}>
                    {dt.toFormat('HH:mm:ss')}
                </div>
                <div style={{
                    color: 'var(--color-text-tertiary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {shot.active_app || '—'}
                </div>
            </div>
        </button>
    );
}
